import type {
  EscalationRecord,
  HumanMessage,
  UserMessage,
} from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/escalation";
import type { ThreadEscalationInfo } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/message-merchant-support-agent";
import { useHandler } from "@redotech/react-util/hook";
import { useStore } from "@redotech/react-util/store";
import { showNotification } from "@redotech/redo-web/alert";
import { dismissNotificationByKey } from "@redotech/redo-web/notification-provider";
import { localStore } from "@redotech/web-util/storage";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import type {
  EscalationContextType,
  EscalationStatus,
} from "./escalation-context";
import { sanitizeIntercomBody } from "./intercom-body";

const ESCALATION_NOTIFICATION_KEY = "help-chat-escalation-human-message";
const ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH = 140;
const dismissedEscalationStore = localStore(
  "redo.escalation-notification-dismissed",
);

export function clearEscalationNotification() {
  dismissNotificationByKey(ESCALATION_NOTIFICATION_KEY);
}

interface EscalationThread {
  _id: string;
  escalation?: ThreadEscalationInfo;
}

interface UseEscalationNotificationsParams<T extends EscalationThread> {
  isOpen: boolean;
  open: () => void;
  threads: readonly T[];
  escalationContext: EscalationContextType;
  merchantRpcClient: {
    checkActiveEscalation: (params: {}) => Promise<{
      activeEscalation: {
        escalationId: string;
        triageThreadId: string;
        status: EscalationStatus;
      } | null;
    }>;
    getEscalation: (params: {
      escalationId: string;
    }) => Promise<{ escalation: EscalationRecord | null }>;
    getEscalationMessages: (params: {
      escalationId: string;
    }) => Promise<{
      status: string;
      messages: HumanMessage[];
      userMessages: UserMessage[];
    }>;
  };
  /**
   * Agent-specific thread loader. The hook needs to fetch the
   * candidate thread when the user clicks the toast — agents differ
   * in which RPC backs the list, so it's injected.
   */
  getThreads: (params: { userEmail: string }) => Promise<{ threads: T[] }>;
  loadThreadIntoChat: (threadId: string, thread: T) => Promise<void>;
  userEmail: string | undefined;
  hasInitializedRef: MutableRefObject<boolean>;
  pendingThreadLoadInFlightRef: MutableRefObject<boolean>;
}

export function useEscalationNotifications<T extends EscalationThread>({
  isOpen,
  open,
  threads,
  escalationContext,
  merchantRpcClient,
  getThreads,
  loadThreadIntoChat,
  userEmail,
  hasInitializedRef,
  pendingThreadLoadInFlightRef,
}: UseEscalationNotificationsParams<T>) {
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [pendingThreadToOpen, setPendingThreadToOpen] = useState<string | null>(
    null,
  );
  const hasRecoveredEscalationRef = useRef(false);
  const [dismissedEscalationId, setDismissedEscalationId] = useStore(
    dismissedEscalationStore,
  );

  const { setEscalation, setTriageThreadId } = escalationContext;

  const openEscalatedThreadFromNotification = useHandler((threadId: string) => {
    hasInitializedRef.current = true;
    clearEscalationNotification();
    setIsLoadingThread(true);
    setPendingThreadToOpen(threadId);
    open();
  });

  const showEscalationNotification = useHandler(
    ({
      title,
      description,
      triageThreadId,
      escalationId,
    }: {
      title: string;
      description: string;
      triageThreadId: string;
      escalationId: string | null;
    }) => {
      dismissNotificationByKey(ESCALATION_NOTIFICATION_KEY);
      showNotification({
        notificationKey: ESCALATION_NOTIFICATION_KEY,
        title,
        description,
        primaryButton: {
          text: "Open chat",
          onClick: () => openEscalatedThreadFromNotification(triageThreadId),
        },
        onDismiss: () => {
          if (escalationId) {
            setDismissedEscalationId(escalationId);
          }
        },
      });
    },
  );

  const notifyClosedEscalationMessage = useHandler((message: HumanMessage) => {
    setDismissedEscalationId(undefined);

    // Toast must route to the ESCALATION's bound thread, not the
    // floating "currently displayed" thread setter. The latter gets
    // overwritten every time the merchant clicks a different
    // conversation in history; using it here means a toast click
    // could land the merchant on the unrelated thread they were
    // browsing, not the support conversation that just got a reply.
    // Fall back to the floating value for help-chat compatibility
    // (it doesn't yet populate `escalation.triageThreadId`).
    const threadId =
      escalationContext.escalation.triageThreadId ??
      escalationContext.triageThreadId;
    if (!threadId) {
      return;
    }

    const singleLineMessage = sanitizeIntercomBody(message.body);
    const description =
      singleLineMessage.length > 0
        ? singleLineMessage.length > ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH
          ? `${singleLineMessage.slice(0, ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}...`
          : singleLineMessage
        : "Support sent a new message";

    showEscalationNotification({
      title: `${message.authorName?.split(" ")[0] || "Support"} sent you a message`,
      description,
      triageThreadId: threadId,
      escalationId: escalationContext.escalation.escalationId,
    });
  });

  // Recover escalation state when panel is closed and context was cleared.
  // `escalationContext.escalation.escalationId` is in this effect's deps AND
  // gets mutated by `setEscalation` below — any `await` after that mutation
  // would yield to React, fire cleanup (cancelled = true), and silently
  // skip the notification. So: all awaits up front, mutate + show last.
  useEffect(() => {
    if (isOpen) {
      return;
    }
    if (escalationContext.escalation.escalationId) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const { activeEscalation } =
          await merchantRpcClient.checkActiveEscalation({});

        if (cancelled || !activeEscalation) {
          return;
        }

        const { escalation: fullEscalation } =
          await merchantRpcClient.getEscalation({
            escalationId: activeEscalation.escalationId,
          });

        if (cancelled || !fullEscalation) {
          return;
        }

        const shouldShowNotification =
          !hasRecoveredEscalationRef.current &&
          (activeEscalation.status === "human-active" ||
            activeEscalation.status === "waiting") &&
          dismissedEscalationId !== activeEscalation.escalationId;

        let lastAgentMessage: HumanMessage | undefined;
        if (
          shouldShowNotification &&
          activeEscalation.status === "human-active"
        ) {
          try {
            const result = await merchantRpcClient.getEscalationMessages({
              escalationId: activeEscalation.escalationId,
            });
            if (cancelled) {
              return;
            }
            lastAgentMessage =
              result.messages.length > 0
                ? result.messages.reduce((latest, msg) =>
                    msg.createdAt > latest.createdAt ? msg : latest,
                  )
                : undefined;
          } catch {
            // Fall through to generic "Support agent responded" copy.
          }
        }
        if (cancelled) {
          return;
        }

        setTriageThreadId(activeEscalation.triageThreadId);
        setEscalation({
          escalationId: activeEscalation.escalationId,
          noticeTopic: fullEscalation.noticeTopic,
          status: activeEscalation.status,
          intercomConversationId: fullEscalation.intercomConversationId,
          // Without the bound triageThreadId the layout-level Ask
          // Redo watcher can subscribe to SSE just fine but won't
          // know which cached chat to route live human messages
          // into — the watcher reads `escalation.triageThreadId`,
          // not the floating one.
          triageThreadId: activeEscalation.triageThreadId,
        });

        if (shouldShowNotification) {
          if (activeEscalation.status === "waiting") {
            showEscalationNotification({
              title: "Our team is looking into this",
              description: "We'll respond shortly.",
              triageThreadId: activeEscalation.triageThreadId,
              escalationId: activeEscalation.escalationId,
            });
          } else if (lastAgentMessage) {
            const authorName =
              lastAgentMessage.authorName?.split(" ")[0] || "Support";
            const body = sanitizeIntercomBody(lastAgentMessage.body);
            const description =
              body.length > ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH
                ? `${body.slice(0, ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}...`
                : body || "Sent you a message";

            showEscalationNotification({
              title: `${authorName} sent you a message`,
              description,
              triageThreadId: activeEscalation.triageThreadId,
              escalationId: activeEscalation.escalationId,
            });
          } else {
            showEscalationNotification({
              title: "Support agent responded",
              description: "A support agent has replied to your escalation",
              triageThreadId: activeEscalation.triageThreadId,
              escalationId: activeEscalation.escalationId,
            });
          }
        }
        hasRecoveredEscalationRef.current = true;
      } catch (err) {
        // Server check failed — don't restore stale context. Log so
        // a silent recovery failure (e.g. checkActiveEscalation 5xx)
        // shows up in DevTools instead of "the toast never appeared".
        console.error("[escalation] notification recovery failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    escalationContext.escalation.escalationId,
    setTriageThreadId,
    setEscalation,
    dismissedEscalationId,
    showEscalationNotification,
    merchantRpcClient,
  ]);

  // Handle pending thread open (from notification click)
  useEffect(() => {
    if (!isOpen || !pendingThreadToOpen) {
      return;
    }
    if (pendingThreadLoadInFlightRef.current) {
      return;
    }

    const loadPendingThread = async () => {
      pendingThreadLoadInFlightRef.current = true;
      if (!userEmail) {
        setPendingThreadToOpen(null);
        pendingThreadLoadInFlightRef.current = false;
        return;
      }
      try {
        const cachedThread = threads.find((t) => t._id === pendingThreadToOpen);
        if (cachedThread) {
          await loadThreadIntoChat(pendingThreadToOpen, cachedThread);
          return;
        }

        const latestThreads = await getThreads({ userEmail });
        const freshThread = latestThreads.threads.find(
          (t) => t._id === pendingThreadToOpen,
        );
        if (freshThread) {
          await loadThreadIntoChat(pendingThreadToOpen, freshThread);
        } else {
          setIsLoadingThread(false);
        }
      } finally {
        setPendingThreadToOpen(null);
        pendingThreadLoadInFlightRef.current = false;
      }
    };

    void loadPendingThread();
  }, [
    isOpen,
    pendingThreadToOpen,
    threads,
    loadThreadIntoChat,
    merchantRpcClient,
    userEmail,
    getThreads,
    pendingThreadLoadInFlightRef,
  ]);

  // Clear notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      clearEscalationNotification();
    }
  }, [isOpen]);

  return {
    isLoadingThread,
    setIsLoadingThread,
    openEscalatedThreadFromNotification,
    notifyClosedEscalationMessage,
    dismissedEscalationId,
    setDismissedEscalationId,
  };
}
